import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from 'react';
import {
  ReactFlow,
  Background,
  MiniMap,
  Panel,
  useReactFlow,
  useViewport,
  ReactFlowProvider,
  NodeProps,
  EdgeTypes,
  SelectionMode,
  type OnConnectStart,
  type OnConnectEnd,
  type Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './workflow-styles.css';
import {
  AnnotationIcon,
  ChatAlt2Icon,
  CollectionIcon,
  DocumentTextIcon,
  FilmIcon,
  PhotographIcon,
  SparklesIcon,
  MusicNoteIcon,
  UploadIcon,
  VideoCameraIcon,
  ViewGridIcon,
  VolumeUpIcon,
  ZoomInIcon,
} from '@heroicons/react/outline';

import useLocale from 'next-translate/useTranslation';
import { useWorkflowStore } from '@/lib/workflows/store';
import { nodeTypes as baseNodeTypes } from './nodes';
import { getGroupColor } from './nodes/GroupNode';
import CollaboratorCursors from './CollaboratorCursors';
import { CustomEdge } from './edges/CustomEdge';
import { WorkflowToolbar } from './WorkflowToolbar';
import { ZoomControls } from './ZoomControls';
import { executeSingleNode } from '@/lib/workflows/executor';
import clsxm from '@/lib/clsxm';
import { useYjsCollaboration } from '@/lib/workflows/useYjsCollaboration';
import { useYjsAwareness } from '@/lib/workflows/useYjsAwareness';
import { TextPresetSelectorModal } from './TextPresetSelectorModal';
import { createInitialTextPresetFields } from '@/lib/workflows/textPresets';
import type { TextPresetOption } from '@/lib/workflows/textPresets';
import { ConnectionEndMenu } from './ConnectionEndMenu';
import {
  getCompatibleNodes,
  type CompatibleNodeOption,
} from '@/lib/workflows/nodeCompatibility';
import {
  getDataTypeFromHandle,
  type HandleDataType,
} from '@/lib/workflows/store';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface WorkflowCanvasProps {
  onUseTemplate?: () => void;
  readOnly?: boolean;
}

function WorkflowCanvasInner({
  onUseTemplate,
  readOnly = false,
}: WorkflowCanvasProps) {
  const { t } = useLocale('common');
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  // Zustand store
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);
  const onNodesChange = useWorkflowStore((state) => state.onNodesChange);
  const onEdgesChange = useWorkflowStore((state) => state.onEdgesChange);
  const onConnect = useWorkflowStore((state) => state.onConnect);
  const addNode = useWorkflowStore((state) => state.addNode);
  const addNodeAndConnect = useWorkflowStore(
    (state) => state.addNodeAndConnect,
  );
  const setSelectedNode = useWorkflowStore((state) => state.setSelectedNode);
  const undo = useWorkflowStore((state) => state.undo);
  const redo = useWorkflowStore((state) => state.redo);
  const canUndo = useWorkflowStore((state) => state.canUndo);
  const canRedo = useWorkflowStore((state) => state.canRedo);
  const groupNodes = useWorkflowStore((state) => state.groupNodes);
  const ungroupNodes = useWorkflowStore((state) => state.ungroupNodes);
  const duplicateNode = useWorkflowStore((state) => state.duplicateNode);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);

  const { x: vpX, y: vpY, zoom } = useViewport();

  const [showNodePalette, setShowNodePalette] = useState(false);
  const [showTextPresetModal, setShowTextPresetModal] = useState(false);
  const [interactionMode, setInteractionMode] = useState<'pan' | 'select'>(
    'pan',
  );

  // Tracks the handle the user is currently dragging an edge from. Set on
  // onConnectStart and read on onConnectEnd to compute compatible nodes for
  // the connection-drop menu.
  const connectingFromRef = useRef<{
    nodeId: string;
    handleId: string | null;
    handleType: 'source' | 'target';
    dataType: HandleDataType;
  } | null>(null);

  // When set, renders the connection-drop menu at the given screen position.
  const [connectionMenu, setConnectionMenu] = useState<{
    position: { x: number; y: number };
    flowPosition: { x: number; y: number };
    dataType: HandleDataType;
    direction: 'source' | 'target';
    options: CompatibleNodeOption[];
    fromNodeId: string;
    fromHandleId: string | null;
  } | null>(null);

  // Bounding box of selected groupable nodes (for floating group button)
  const selectionBounds = useMemo(() => {
    const selected = nodes.filter(
      (n) => n.selected && n.type !== 'group' && !n.parentId,
    );
    if (selected.length < 2) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    for (const node of selected) {
      const w = (node.measured as any)?.width ?? 280;
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + w);
    }
    return { minX, minY, maxX };
  }, [nodes]);

  // Use Yjs for real-time collaboration with CRDT-based conflict resolution
  const { provider, awareness } = useYjsCollaboration();
  const collaborators = useYjsAwareness(awareness);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd (Mac) or Ctrl (Windows/Linux)
      const isMod = event.metaKey || event.ctrlKey;

      if (isMod && event.key === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          // Redo: Cmd/Ctrl + Shift + Z
          if (canRedo) {
            redo();
          }
        } else {
          // Undo: Cmd/Ctrl + Z
          if (canUndo) {
            undo();
          }
        }
      }

      // Alternative redo shortcut: Cmd/Ctrl + Y
      if (isMod && event.key === 'y') {
        event.preventDefault();
        if (canRedo) {
          redo();
        }
      }

      // Group selected nodes: Cmd/Ctrl + G
      if (isMod && !event.shiftKey && event.key === 'g') {
        event.preventDefault();
        const currentNodes = useWorkflowStore.getState().nodes;
        const selectedIds = currentNodes
          .filter((n) => n.selected && n.type !== 'group' && !n.parentId)
          .map((n) => n.id);
        if (selectedIds.length >= 2) {
          groupNodes(selectedIds);
        }
      }

      // Ungroup: Cmd/Ctrl + Shift + G
      if (isMod && event.shiftKey && event.key === 'G') {
        event.preventDefault();
        const currentNodes = useWorkflowStore.getState().nodes;
        const selectedGroups = currentNodes.filter(
          (n) => n.selected && n.type === 'group',
        );
        selectedGroups.forEach((g) => ungroupNodes(g.id));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, canUndo, canRedo, groupNodes, ungroupNodes]);

  // Available node types to add
  const availableNodes: {
    type: string;
    label: string;
    icon: React.ReactNode;
    description: string;
  }[] = [
    {
      type: 'input',
      label: t('Text Input'),
      icon: <DocumentTextIcon className='w-5 h-5 text-primary-500' />,
      description: t('Add initial text data or prompts'),
    },
    {
      type: 'upload',
      label: t('Upload Media'),
      icon: <UploadIcon className='w-5 h-5 text-primary-500' />,
      description: t('Upload images, video, or audio files'),
    },
    {
      type: 'select-image',
      label: t('Select Media'),
      icon: <PhotographIcon className='w-5 h-5 text-primary-500' />,
      description: t('Select from generated images or videos'),
    },
    {
      type: 'image-generation',
      label: t('Text to Image'),
      icon: <SparklesIcon className='w-5 h-5 text-primary-500' />,
      description: t('Generate images from text prompts'),
    },
    {
      type: 'image-edit',
      label: t('Image to Image'),
      icon: <FilmIcon className='w-5 h-5 text-primary-500' />,
      description: t('Transform images with AI'),
    },
    {
      type: 'image-upscaler',
      label: t('Upscale Image'),
      icon: <ZoomInIcon className='w-5 h-5 text-primary-500' />,
      description: t('Upscale and enhance images with AI'),
    },
    {
      type: 'image-splitter',
      label: t('Image Splitter'),
      icon: <ViewGridIcon className='w-5 h-5 text-primary-500' />,
      description: t('Split an image into a grid of smaller images'),
    },
    {
      type: 'text-to-video',
      label: t('Text to Video'),
      icon: <VideoCameraIcon className='w-5 h-5 text-primary-500' />,
      description: t('Generate videos from text prompts'),
    },
    {
      type: 'image-to-video',
      label: t('Image to Video'),
      icon: <FilmIcon className='w-5 h-5 text-primary-500' />,
      description: t('Generate videos from images'),
    },
    {
      type: 'first-last-frame-video',
      label: t('First-Last Frame to Video'),
      icon: <VideoCameraIcon className='w-5 h-5 text-primary-500' />,
      description: t('Generate videos from first and last frame'),
    },
    {
      type: 'references-to-video',
      label: t('References to Video'),
      icon: <FilmIcon className='w-5 h-5 text-primary-500' />,
      description: t(
        'Generate videos from reference images for consistent characters, objects, or style',
      ),
    },
    {
      type: 'video-merge',
      label: t('Merge Videos'),
      icon: <CollectionIcon className='w-5 h-5 text-primary-500' />,
      description: t('Merge multiple videos into one'),
    },
    {
      type: 'text-generation',
      label: t('Text Generation'),
      icon: <ChatAlt2Icon className='w-5 h-5 text-primary-500' />,
      description: t('Generate text with AI models'),
    },
    {
      type: 'text-preset',
      label: t('Text Preset'),
      icon: <CollectionIcon className='w-5 h-5 text-primary-500' />,
      description: t('Choose from 300+ preset tools for specific text tasks'),
    },
    {
      type: 'text-to-speech',
      label: t('Text to Speech'),
      icon: <VolumeUpIcon className='w-5 h-5 text-primary-500' />,
      description: t('Convert text to realistic speech'),
    },
    {
      type: 'music-generation',
      label: t('Music Generation'),
      icon: <MusicNoteIcon className='w-5 h-5 text-primary-500' />,
      description: t('Generate AI songs with lyrics or instrumental music'),
    },
    {
      type: 'talking-video',
      label: t('Talking Video'),
      icon: <VideoCameraIcon className='w-5 h-5 text-primary-500' />,
      description: t(
        'Animate an image or video so it speaks an audio clip or generated voiceover',
      ),
    },
    {
      type: 'note',
      label: t('Note'),
      icon: <AnnotationIcon className='w-5 h-5 text-primary-500' />,
      description: t('Add documentation'),
    },
  ];

  // Drag-from-handle → drop-on-canvas → show "Add compatible node" menu.
  // Captures the dragged handle so onConnectEnd can decide what to offer.
  const onConnectStart = useCallback<OnConnectStart>(
    (_event, params) => {
      if (!params.nodeId || !params.handleType) {
        connectingFromRef.current = null;
        return;
      }
      const dataType = getDataTypeFromHandle(params.handleId ?? null);
      connectingFromRef.current = {
        nodeId: params.nodeId,
        handleId: params.handleId ?? null,
        handleType: params.handleType,
        dataType,
      };
      // Drive the animated-dashed connection line color via CSS — see
      // [data-connecting-type] selectors in workflow-styles.css.
      if (reactFlowWrapper.current) {
        reactFlowWrapper.current.dataset.connectingType = dataType;
      }
      // Close the standard palette so the two menus don't fight
      if (showNodePalette) setShowNodePalette(false);
    },
    [showNodePalette],
  );

  const onConnectEnd = useCallback<OnConnectEnd>(
    (event) => {
      const start = connectingFromRef.current;
      connectingFromRef.current = null;
      if (reactFlowWrapper.current) {
        delete reactFlowWrapper.current.dataset.connectingType;
      }
      if (!start || readOnly) return;

      // Only show the menu if the drop landed on the empty pane (not on
      // another handle/node).
      const target = (event.target as Partial<Element> | null) ?? null;
      const targetIsPane =
        !!target?.classList && target.classList.contains('react-flow__pane');
      if (!targetIsPane) return;

      // The event is either a MouseEvent or TouchEvent at this point.
      const clientX =
        'clientX' in event
          ? event.clientX
          : (event.changedTouches?.[0]?.clientX ?? 0);
      const clientY =
        'clientY' in event
          ? event.clientY
          : (event.changedTouches?.[0]?.clientY ?? 0);

      const options = getCompatibleNodes(start.dataType, start.handleType);
      if (options.length === 0) return;

      setConnectionMenu({
        position: { x: clientX, y: clientY },
        flowPosition: screenToFlowPosition({ x: clientX, y: clientY }),
        dataType: start.dataType,
        direction: start.handleType,
        options,
        fromNodeId: start.nodeId,
        fromHandleId: start.handleId,
      });
    },
    [readOnly, screenToFlowPosition],
  );

  // Handle node addition
  const addNodeWithConfig = useCallback(
    (nodeType: string, nodeLabel: string, config: Record<string, any> = {}) => {
      const id = `${nodeType}-${Date.now()}`;

      // Calculate position at center of current viewport with smart cascading
      // Use a smaller offset and diagonal pattern for better UX
      const cascadeOffset = (nodes.length % 10) * 30; // Reset offset every 10 nodes

      const position = reactFlowWrapper.current
        ? screenToFlowPosition({
            // Center of the visible canvas area (accounting for toolbar ~60px)
            x: window.innerWidth / 2,
            y: (window.innerHeight - 60) / 2,
          })
        : { x: 250, y: 250 };

      // Add small cascade offset to prevent exact overlaps
      const newPosition = {
        x: position.x + cascadeOffset,
        y: position.y + cascadeOffset,
      };

      const newNode = {
        id,
        type: nodeType,
        position: newPosition,
        data: {
          label: nodeLabel,
          type: nodeType,
          config,
        },
      };

      addNode(newNode);
    },
    [addNode, screenToFlowPosition, nodes.length],
  );

  const handleAddNode = useCallback(
    (nodeType: string, nodeLabel: string) => {
      if (nodeType === 'text-preset') {
        setShowNodePalette(false);
        setShowTextPresetModal(true);
        return;
      }

      addNodeWithConfig(nodeType, nodeLabel);
      setShowNodePalette(false);
    },
    [addNodeWithConfig],
  );

  const handleTextPresetSelect = useCallback(
    (preset: TextPresetOption) => {
      const initialFields = createInitialTextPresetFields(preset.fields);
      addNodeWithConfig('text-preset', preset.label, {
        presetSlug: preset.value,
        fields: Object.fromEntries(initialFields),
      });
      setShowTextPresetModal(false);
    },
    [addNodeWithConfig],
  );

  // User picked a node from the connection-drop menu — create it and wire it
  // up to the originating handle in a single undoable action.
  const handleConnectionMenuSelect = useCallback(
    (option: CompatibleNodeOption) => {
      const menu = connectionMenu;
      if (!menu) return;

      // text-preset needs a preset chosen first; defer to the modal but skip
      // the auto-connection (the user will land on a freshly created node).
      if (option.type === 'text-preset') {
        setConnectionMenu(null);
        setShowTextPresetModal(true);
        return;
      }

      const newNodeId = `${option.type}-${Date.now()}`;

      // Anchor the new node so the matching handle sits near the cursor.
      // For a source-drag (output → menu), the new node's left/target side
      // should align with the cursor → origin [0, 0.5].
      // For a target-drag (input → menu), the right/source side aligns →
      // origin [1, 0.5].
      const origin: [number, number] =
        menu.direction === 'source' ? [0, 0.5] : [1, 0.5];

      const newNode = {
        id: newNodeId,
        type: option.type,
        position: menu.flowPosition,
        origin,
        data: {
          label: option.defaultLabel,
          type: option.type,
          config: {},
        },
      };

      const connection: Connection =
        menu.direction === 'source'
          ? {
              source: menu.fromNodeId,
              sourceHandle: menu.fromHandleId,
              target: newNodeId,
              targetHandle: option.matchedHandle.id,
            }
          : {
              source: newNodeId,
              sourceHandle: option.matchedHandle.id,
              target: menu.fromNodeId,
              targetHandle: menu.fromHandleId,
            };

      addNodeAndConnect(newNode, connection);
      setConnectionMenu(null);
    },
    [connectionMenu, addNodeAndConnect],
  );

  // Broadcast cursor position (throttled) and click via awareness
  useEffect(() => {
    if (!awareness) return;
    const lastSentRef = { ts: 0 };

    const handlePointerMove = (e: PointerEvent) => {
      const now = Date.now();
      if (now - lastSentRef.ts < 50) return; // throttle ~20Hz
      lastSentRef.ts = now;
      const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      awareness.setLocalStateField('cursor', { x: pos.x, y: pos.y });
    };

    const handleClick = (e: MouseEvent) => {
      const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      awareness.setLocalStateField('click', {
        x: pos.x,
        y: pos.y,
        at: Date.now(),
      });
    };

    const el = reactFlowWrapper.current;
    if (el) {
      el.addEventListener('pointermove', handlePointerMove);
      el.addEventListener('click', handleClick);
    }
    return () => {
      if (el) {
        el.removeEventListener('pointermove', handlePointerMove);
        el.removeEventListener('click', handleClick);
      }
    };
  }, [awareness, screenToFlowPosition]);

  // Handle node selection
  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: any) => {
      setSelectedNode(node.id);
    },
    [setSelectedNode],
  );

  // Handle pane click (deselect)
  const handlePaneClick = useCallback(() => {
    setSelectedNode(null);
  }, [setSelectedNode]);

  // Handle running a single node
  const handleRunNode = useCallback((nodeId: string) => {
    // Run asynchronously but keep a void return type to match node props
    (async () => {
      try {
        // Get current nodes and edges from store to avoid dependency issues
        const currentNodes = useWorkflowStore.getState().nodes;
        const currentEdges = useWorkflowStore.getState().edges;
        await executeSingleNode(nodeId, currentNodes, currentEdges);
      } catch (error: any) {
        console.error('Node execution failed:', error);
      }
    })();
  }, []); // No dependencies - prevents re-creation on every node update

  // Wrap node types to inject onRunNode prop
  const nodeTypes = useMemo(() => {
    const wrappedTypes: any = {};

    Object.keys(baseNodeTypes).forEach((key) => {
      const NodeComponent = baseNodeTypes[key as keyof typeof baseNodeTypes];
      wrappedTypes[key] = (props: NodeProps) => {
        const NodeWithExtras = NodeComponent as React.ComponentType<
          NodeProps & {
            onRunNode?: (nodeId: string) => void;
            nodeId?: string;
            readOnly?: boolean;
          }
        >;
        return (
          <NodeWithExtras
            {...props}
            onRunNode={readOnly ? undefined : handleRunNode}
            nodeId={props.id}
            readOnly={readOnly}
          />
        );
      };
    });

    return wrappedTypes;
  }, [handleRunNode, readOnly]);

  // Edge types with custom delete button
  const edgeTypes: EdgeTypes = useMemo(
    () => ({
      default: CustomEdge,
    }),
    [],
  );

  return (
    <div
      className='w-full h-full flex flex-col bg-gray-50 dark:bg-gray-900'
      ref={reactFlowWrapper}
    >
      {/* Toolbar */}
      <WorkflowToolbar
        onAddNode={() => setShowNodePalette(!showNodePalette)}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        awareness={awareness}
        onUseTemplate={onUseTemplate}
        readOnly={readOnly}
        interactionMode={interactionMode}
        onToggleMode={() =>
          setInteractionMode((m) => (m === 'pan' ? 'select' : 'pan'))
        }
      />

      {/* React Flow Canvas */}
      <div className='flex-1 relative'>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={readOnly ? undefined : onNodesChange}
          onEdgesChange={readOnly ? undefined : onEdgesChange}
          onConnect={readOnly ? undefined : onConnect}
          onConnectStart={readOnly ? undefined : onConnectStart}
          onConnectEnd={readOnly ? undefined : onConnectEnd}
          onNodeClick={readOnly ? undefined : handleNodeClick}
          onPaneClick={readOnly ? undefined : handlePaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
          minZoom={0.2}
          maxZoom={3}
          fitView
          fitViewOptions={{ padding: 0.3, maxZoom: 1.2 }}
          proOptions={{ hideAttribution: true }}
          className='bg-gray-50 dark:bg-gray-900'
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={!readOnly}
          selectionOnDrag={interactionMode === 'select'}
          panOnDrag={interactionMode === 'select' ? [1, 2] : true}
          selectionMode={SelectionMode.Partial}
          panOnScroll
          zoomOnScroll={false}
          zoomOnPinch
        >
          <Background
            className='dark:opacity-40'
            color='#9ca3af'
            gap={16}
            size={1.5}
          />
          <MiniMap
            pannable
            zoomable
            nodeColor={(node) => {
              switch (node.type) {
                case 'input':
                  return '#9ca3af';
                case 'upload':
                  return '#8b5cf6';
                case 'select-image':
                  return '#3b82f6';
                case 'image-generation':
                  return '#a855f7';
                case 'image-edit':
                  return '#3b82f6';
                case 'image-upscaler':
                  return '#10b981';
                case 'image-splitter':
                  return '#8b5cf6';
                case 'text-to-video':
                  return '#06b6d4';
                case 'image-to-video':
                  return '#6366f1';
                case 'first-last-frame-video':
                  return '#06b6d4';
                case 'references-to-video':
                  return '#0891b2';
                case 'text-generation':
                  return '#14b8a6';
                case 'text-to-speech':
                  return '#6366f1';
                case 'music-generation':
                  return '#d97706';
                case 'talking-video':
                  return '#db2777';
                case 'note':
                  return '#f59e0b';
                case 'group':
                  return getGroupColor((node.data as { color?: string })?.color)
                    .minimap;
                default:
                  return '#6b7280';
              }
            }}
            className='bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm'
          />

          {/* Custom Zoom Controls */}
          <ZoomControls />

          {/* Floating selection toolbar — positioned above the selection */}
          {selectionBounds && !readOnly && (
            <div
              className='absolute z-50 pointer-events-auto'
              style={{
                left:
                  ((selectionBounds.minX + selectionBounds.maxX) / 2) * zoom +
                  vpX,
                top: selectionBounds.minY * zoom + vpY - 52,
                transform: 'translateX(-50%)',
              }}
            >
              <div className='flex items-center gap-1 bg-white dark:bg-gray-800 px-2 py-1.5 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700'>
                {/* Group */}
                <SimpleTooltip title='Group' side='top'>
                  <button
                    onClick={() => {
                      const selectedIds = nodes
                        .filter(
                          (n) =>
                            n.selected && n.type !== 'group' && !n.parentId,
                        )
                        .map((n) => n.id);
                      if (selectedIds.length >= 2) {
                        groupNodes(selectedIds);
                      }
                    }}
                    className='p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
                  >
                    <svg
                      className='w-4 h-4 text-blue-500'
                      fill='none'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' />
                    </svg>
                  </button>
                </SimpleTooltip>

                <div className='w-px h-5 bg-gray-200 dark:bg-gray-700' />

                {/* Duplicate */}
                <SimpleTooltip title='Duplicate' side='top'>
                  <button
                    onClick={() => {
                      const selectedIds = nodes
                        .filter((n) => n.selected && n.type !== 'group')
                        .map((n) => n.id);
                      selectedIds.forEach((id) => duplicateNode(id));
                    }}
                    className='p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
                  >
                    <svg
                      className='w-4 h-4 text-gray-600 dark:text-gray-300'
                      fill='none'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' />
                    </svg>
                  </button>
                </SimpleTooltip>

                {/* Delete */}
                <SimpleTooltip title='Delete' side='top'>
                  <button
                    onClick={() => {
                      const selectedIds = nodes
                        .filter((n) => n.selected)
                        .map((n) => n.id);
                      selectedIds.forEach((id) => deleteNode(id));
                    }}
                    className='p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors'
                  >
                    <svg
                      className='w-4 h-4 text-red-500 dark:text-red-400'
                      fill='none'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                    </svg>
                  </button>
                </SimpleTooltip>
              </div>
            </div>
          )}

          {/* Node Palette Overlay */}
          {showNodePalette && !readOnly && (
            <Panel position='top-left' className='m-4' style={{ zIndex: 1000 }}>
              <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 w-80'>
                <div className='flex items-center justify-between mb-4'>
                  <h3 className='font-semibold text-gray-900 dark:text-white'>
                    {t('Add Node')}
                  </h3>
                  <button
                    onClick={() => setShowNodePalette(false)}
                    className='text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  >
                    <svg
                      className='w-5 h-5'
                      fill='none'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path d='M6 18L18 6M6 6l12 12' />
                    </svg>
                  </button>
                </div>

                <div className='space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto pr-2'>
                  {availableNodes.map((node) => (
                    <button
                      key={node.type}
                      onClick={() => handleAddNode(node.type, node.label)}
                      className={clsxm(
                        'w-full text-left px-4 py-3 rounded-lg border-2 border-gray-200 dark:border-gray-700',
                        'hover:border-primary-500 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all',
                        'flex items-center gap-3',
                      )}
                    >
                      <span className='flex-shrink-0'>{node.icon}</span>
                      <div className='flex-1'>
                        <div className='font-medium text-gray-900 dark:text-white text-sm'>
                          {node.label}
                        </div>
                        <div className='text-xs text-gray-500 dark:text-gray-400 mt-0.5'>
                          {node.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </Panel>
          )}
        </ReactFlow>
        {/* Collaborator cursors overlay within ReactFlow context */}
        <div className='pointer-events-none absolute inset-0'>
          <CollaboratorCursors collaborators={collaborators} />
        </div>
      </div>

      <TextPresetSelectorModal
        open={showTextPresetModal}
        onClose={() => setShowTextPresetModal(false)}
        onSelect={handleTextPresetSelect}
      />

      {connectionMenu && !readOnly && (
        <ConnectionEndMenu
          position={connectionMenu.position}
          dataType={connectionMenu.dataType}
          direction={connectionMenu.direction}
          options={connectionMenu.options}
          onSelect={handleConnectionMenuSelect}
          onClose={() => setConnectionMenu(null)}
        />
      )}
    </div>
  );
}

export function WorkflowCanvas(props: WorkflowCanvasProps = {}) {
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
